import { Navigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LockKey } from "@phosphor-icons/react";

function AccessPaused() {
    return (
        <div className="grid h-screen place-items-center bg-paper px-6" data-testid="access-paused">
            <div className="max-w-md text-center">
                <div className="mx-auto grid h-12 w-12 place-items-center rounded-2xl bg-stone-900">
                    <LockKey size={22} weight="fill" className="text-brand" />
                </div>
                <h1 className="mt-5 font-heading text-2xl font-bold">Workspace access paused</h1>
                <p className="mt-2 text-sm text-stone2">Your team's free trial has ended. Please ask your workspace admin to upgrade the plan to restore access.</p>
            </div>
        </div>
    );
}

const FEATURE_LABELS = {
    campaigns: "Outbound Campaigns",
    reports: "Reports & Analytics",
    ai_assistant: "AI Voice Assistant",
    call_transfer: "Call Transfer & Monitoring",
    whatsapp: "WhatsApp Messaging",
    social: "Social Hub",
    ads: "Ad Campaign Manager",
    support_tickets: "Priority Support",
};

const ADDON_FEATURES = ["whatsapp", "social", "ads"];

function FeatureLocked({ feature, isAdmin }) {
    const label = FEATURE_LABELS[feature] || "This feature";
    const isAddon = ADDON_FEATURES.includes(feature);
    return (
        <div className="grid h-screen place-items-center bg-paper px-6" data-testid="feature-locked">
            <div className="max-w-md text-center">
                <div className="mx-auto grid h-12 w-12 place-items-center rounded-2xl bg-stone-900">
                    <LockKey size={22} weight="fill" className="text-brand" />
                </div>
                <h1 className="mt-5 font-heading text-2xl font-bold">
                    {label} {isAddon ? "isn't active" : "is a Professional feature"}
                </h1>
                <p className="mt-2 text-sm text-stone2">
                    {isAddon ? (
                        isAdmin
                            ? "Your free trial for this product has ended, or it isn't part of your plan yet. Add it in Billing to switch it back on — your other products are unaffected."
                            : `Ask your workspace admin to add ${label} to your plan.`
                    ) : (
                        <>Your workspace is on the <b>Essential</b> plan. {isAdmin
                            ? "Upgrade to Professional to unlock it — you'll only pay the prorated difference for the rest of your billing cycle."
                            : "Ask your workspace admin to upgrade to Professional to unlock it."}</>
                    )}
                </p>
                {isAdmin && (
                    <Link
                        to="/billing"
                        data-testid="feature-upgrade-btn"
                        className="mt-6 inline-flex rounded-full bg-brand px-6 py-2.5 font-heading text-sm font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-brand-hover"
                    >
                        {isAddon ? "Go to Billing" : "Upgrade to Professional"}
                    </Link>
                )}
            </div>
        </div>
    );
}

function NoProductAccess({ product }) {
    const label = { calling: "Calling", whatsapp: "WhatsApp", social: "Social" }[product] || "This product";
    return (
        <div className="grid h-screen place-items-center bg-paper px-6" data-testid="no-product-access">
            <div className="max-w-md text-center">
                <div className="mx-auto grid h-12 w-12 place-items-center rounded-2xl bg-stone-900">
                    <LockKey size={22} weight="fill" className="text-brand" />
                </div>
                <h1 className="mt-5 font-heading text-2xl font-bold">No access to {label}</h1>
                <p className="mt-2 text-sm text-stone2">
                    Your account isn't assigned to {label} in this workspace. Ask your workspace admin to grant you access under Settings → Team.
                </p>
            </div>
        </div>
    );
}

export default function ProtectedRoute({ children, adminOnly = false, roles = null, feature = null, product = null }) {
    const { user } = useAuth();
    const location = useLocation();
    if (user === null) {
        return (
            <div className="grid h-screen place-items-center text-sm text-stone2" data-testid="auth-loading">
                Loading…
            </div>
        );
    }
    if (!user) return <Navigate to="/login" replace />;
    // Super admin only ever sees the platform console.
    if (user.role === "super_admin" && !(roles && roles.includes("super_admin")))
        return <Navigate to="/platform" replace />;
    if (adminOnly && user.role !== "admin") return <Navigate to="/" replace />;
    if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;

    // Subscription lockout: unentitled workspaces (expired trial, no payment) are fully
    // locked out except the Billing page. Admins are routed there; others see a paused notice.
    if (user.role !== "super_admin" && user.entitled === false) {
        if (user.role === "admin") {
            if (location.pathname !== "/billing") return <Navigate to="/billing" replace />;
        } else {
            return <AccessPaused />;
        }
    }

    // Standalone WhatsApp workspaces have no call center — keep them in the WhatsApp/admin area.
    if (user.role !== "super_admin" && user.features?.call_center === false) {
        const allowed = ["/", "/whatsapp", "/social", "/activity", "/settings", "/billing"];
        const ok = allowed.some((p) => location.pathname === p || (p !== "/" && location.pathname.startsWith(p + "/")));
        if (!ok) return <Navigate to="/" replace />;
    }

    // Plan feature gate: Professional-tier features are locked on the Essential plan.
    if (feature && user.role !== "super_admin" && !(user.features || {})[feature]) {
        return <FeatureLocked feature={feature} isAdmin={user.role === "admin"} />;
    }

    // Per-teammate product access (calling / whatsapp / social). Admins always pass.
    if (product && !["admin", "super_admin"].includes(user.role)) {
        const access = user.product_access || ["calling", "whatsapp", "social"];
        if (!access.includes(product)) return <NoProductAccess product={product} />;
    }
    return children;
}
